import React, { memo } from 'react';
import {
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
} from 'react-beautiful-dnd';
import { RawDraftContentState } from 'draft-js';
import { isEmpty } from 'lodash';
import { IconDragIndicator } from '@bynder/icons';
import styled from 'styled-components';
import { Button as DesignSystemButton } from '@bynder/design-system';
import assertNever from '../../helpers/assertNever';
import {
  AudioWidget as Audio,
  ColorPaletteWidget as ColorPalette,
  ImageWidget as Image,
  TableWidget as Table,
  TextWidget as Text,
  TeaWidget as Tea,
  TitleWidget as Title,
  SpacerWidget as Spacer,
  VideoWidget as Video,
  WIDGET_TYPE,
  PersonalFileWidget as PersonalFile,
  WidgetTypes,
  ColorCode,
  ButtonWidget as TButtonWidget,
  CardWidget as Card,
  YoutubeWidget as TYoutubeWidget,
  VimeoWidget as Vimeo,
  FigmaWidget as Figma,
} from '../../types';
import { StyledWidgetV2 } from '../shared/styled-components/Widgets';
import AudioWidget from './AudioWidget';
import { WidgetPosition } from '../../constants/widgets';
import { CollectionWidget as Collection } from '../../types/widgets/collections';
import SpacerWidget from './SpacerWidget';
import CollectionWidget from './collections/CollectionWidget';
import ColorPaletteWidget from './ColorPaletteWidget';
import TextWidget from './TextWidget';
import ImageWidget from './ImageWidget';
import PersonalFileWidget from './PersonalFileWidget';
import { useTableWidgetMutations } from '../../mutations/widgets/table';
import { useColorPaletteWidgetMutations } from '../../mutations/widgets/colorPalette';
import VideoWidget from './VideoWidget';
import { useAudioWidgetMutations } from '../../mutations/widgets/audio';
import { useSpacerWidgetMutations } from '../../mutations/widgets/spacer';
import TableWidget from './TableWidget';
import { InspectorSetting } from '../../constants/guides';
import TitleWidget from './TitleWidget';
import { useGuideStore } from '../../stores/guideStore';
import { useInspectorStore } from '../../stores/inspectorStore';
import ButtonWidget from './button/ButtonWidget';
import CardWidget from './CardWidget';
import { useActiveWidget } from '../../queries/guides';
import YoutubeWidget from './YoutubeWidget';
import VimeoWidget from './VimeoWidget';
import FigmaWidget from './FigmaWidget';
import TeaWidget from './TeaWidget.tsx';
import ImageCroppedWidget from './ImageCroppedWidget.tsx';
import { isImageCropped } from '@bynder/react-image-cropper-lib';

interface Props {
  index: number;
  sectionId: string;
  columnIndex: number;
  widget: WidgetTypes;
  widgetPosition: WidgetPosition;
  numberOfColumns: number;
}

const RenderWidgetV2: React.FC<Props> = props => {
  const {
    index,
    widget,
    sectionId,
    columnIndex,
    numberOfColumns,
    widgetPosition,
  } = props;

  const isEditMode = useGuideStore(s => s.isEditMode);
  const setActiveSectionId = useGuideStore(s => s.setActiveSectionId);
  const setIsInspectorOpen = useInspectorStore(s => s.setIsInspectorOpen);
  const setActiveInspectorSetting = useInspectorStore(
    s => s.setActiveInspectorSetting,
  );
  const activeInspectorSetting = useInspectorStore(
    s => s.activeInspectorSetting,
  );

  const widgetSettings = useInspectorStore(s => s.widgetSettings);
  const setWidgetSettings = useInspectorStore(s => s.setWidgetSettings);
  const activeWidget = useActiveWidget();
  const readOnly = !isEditMode;

  const { addRow, addColumn, removeRow, removeColumn, editCell } =
    useTableWidgetMutations(sectionId, columnIndex, widget?.id);

  const { addColor, removeColor, editColor } = useColorPaletteWidgetMutations(
    sectionId,
    columnIndex,
    widget.id,
  );

  const { resizeSpacerWidget } = useSpacerWidgetMutations(
    sectionId,
    columnIndex,
    widget.id,
  );

  const { updateAudioWidget } = useAudioWidgetMutations(
    sectionId,
    columnIndex,
    widget.id,
  );

  const renderWidget = () => {
    switch (widget.type) {
      case WIDGET_TYPE.TITLE: {
        const titleWidget = widget as Title;

        return (
          <TitleWidget
            widgetId={titleWidget.id}
            columnIndex={columnIndex}
            sectionId={sectionId}
            content={titleWidget.title}
            readOnly={readOnly}
          />
        );
      }

      case WIDGET_TYPE.IMAGE: {
        const imageWidget = widget as Image;

        const isCropped = isImageCropped(imageWidget.imageUrl);
        if (isCropped) {
          return (
            <ImageCroppedWidget
              widgetId={imageWidget.id}
              readOnly={readOnly}
              assetName={imageWidget.assetName}
              imageUrl={imageWidget.imageUrl}
              detailUrl={imageWidget.detailUrl}
              assetId={imageWidget.assetId}
              assetExtension={imageWidget.assetExtension}
              numberOfColumns={numberOfColumns}
              imageAction={imageWidget.imageAction}
              widgetStyle={imageWidget?.style?.stylesheet}
            />
          );
        }

        return (
          <ImageWidget
            widgetId={imageWidget.id}
            readOnly={readOnly}
            assetName={imageWidget.assetName}
            imageUrl={imageWidget.imageUrl}
            detailUrl={imageWidget.detailUrl}
            assetId={imageWidget.assetId}
            assetExtension={imageWidget.assetExtension}
            numberOfColumns={numberOfColumns}
            imageAction={imageWidget.imageAction}
            widgetStyle={imageWidget?.style?.stylesheet}
          />
        );
      }

      case WIDGET_TYPE.COLOR_PALETTE: {
        const colorPaletteWidget = widget as ColorPalette;
        const onAddColor = () => {
          addColor.mutate();
        };

        const onRemoveColor = (cIndex: number) => {
          removeColor.mutate({ index: cIndex });
        };

        const onModifyColor = (
          widgetColorName: string,
          widgetColorCodes: ColorCode[],
          widgetColorPosition: number,
        ) => {
          editColor.mutate({
            widgetColorName,
            widgetColorCodes,
            widgetColorPosition,
          });
        };

        const defaultValues = !isEmpty(colorPaletteWidget.defaultColorCodes)
          ? colorPaletteWidget.defaultColorCodes
          : { hex: true, rgb: true, cmyk: true };

        return (
          <ColorPaletteWidget
            onAddColor={onAddColor}
            onRemoveColor={onRemoveColor}
            onModifyColor={onModifyColor}
            readOnly={readOnly}
            defaultColorCodes={defaultValues}
            colors={colorPaletteWidget.colors}
            numberOfColumns={numberOfColumns}
            widgetPosition={widgetPosition}
            widgetStyle={colorPaletteWidget.style.stylesheet}
          />
        );
      }

      case WIDGET_TYPE.VIDEO: {
        const videoWidget = widget as Video;

        return (
          <VideoWidget
            assetId={videoWidget.assetId}
            widgetId={videoWidget.id}
            readOnly={readOnly}
            columnIndex={columnIndex}
            videoUrl={videoWidget.videoUrl}
            sectionId={sectionId}
            coverUrl={videoWidget?.coverUrl}
            controls={videoWidget?.controls}
            autoplay={videoWidget?.autoplay}
            loop={videoWidget?.loop}
            widgetStyle={videoWidget?.style?.stylesheet}
          />
        );
      }
      case WIDGET_TYPE.BUTTON: {
        const buttonWidget = widget as TButtonWidget;

        return (
          <ButtonWidget
            onEditModeClick={handleWidgetClick}
            widget={buttonWidget}
          />
        );
      }
      case WIDGET_TYPE.CARD: {
        const cardWidget = widget as Card;

        return <CardWidget cardWidget={cardWidget} />;
      }

      case WIDGET_TYPE.VIMEO: {
        const vimeoWidget = widget as Vimeo;

        return <VimeoWidget vimeoWidget={vimeoWidget} />;
      }

      case WIDGET_TYPE.TEXT: {
        const textWidget = widget as Text;

        return (
          <TextWidget
            widgetId={textWidget.id}
            sectionId={sectionId}
            columnIndex={columnIndex}
            content={textWidget.text}
            readOnly={readOnly}
          />
        );
      }

      case WIDGET_TYPE.TEA: {
        const teaWidget = widget as Tea;

        return (
          <TeaWidget
            widgetId={teaWidget.id}
            sectionId={sectionId}
            columnIndex={columnIndex}
            readOnly={readOnly}
            document={teaWidget.document}
            html={teaWidget.html}
          />
        );
      }

      case WIDGET_TYPE.TABLE: {
        const tableWidget = widget as Table;

        const onAddRow = (rowIndex: number) => {
          addRow.mutate({ rowIndex });
        };

        const onAddColumn = (newColumnIndex: number) => {
          addColumn.mutate({ columnIndex: newColumnIndex });
        };

        const onRemoveRow = (rIndex: number) => {
          removeRow.mutate({ rowIndex: rIndex });
        };

        const onRemoveColumn = (cIndex: number) => {
          removeColumn.mutate({ columnIndex: cIndex });
        };

        const onUpdateCellContent = (
          rIndex: number,
          cIndex: number,
          content: RawDraftContentState,
        ) => {
          editCell.mutate({ rowIndex: rIndex, columnIndex: cIndex, content });
        };

        return (
          <TableWidget
            handleAddRow={onAddRow}
            handleAddColumn={onAddColumn}
            handleRemoveRow={onRemoveRow}
            handleRemoveColumn={onRemoveColumn}
            handleUpdateCellContent={onUpdateCellContent}
            table={tableWidget.table}
            readOnly={readOnly}
          />
        );
      }

      case WIDGET_TYPE.SPACER: {
        const spacerWidget = widget as Spacer;

        const handleResizeDivider = (widgetHeight: number) => {
          resizeSpacerWidget.mutate({ widgetHeight });
        };

        return (
          <SpacerWidget
            onResize={handleResizeDivider}
            height={spacerWidget.height}
            readOnly={readOnly}
            divider={spacerWidget.divider}
            stylesheet={spacerWidget.style.stylesheet}
          />
        );
      }

      case WIDGET_TYPE.COLLECTION: {
        const collectionWidget = widget as Collection;

        return (
          <CollectionWidget
            widget={collectionWidget}
            collectionId={collectionWidget.collectionId}
            readOnly={readOnly}
            numberOfColumns={numberOfColumns}
            widgetPosition={widgetPosition}
          />
        );
      }

      case WIDGET_TYPE.AUDIO: {
        const audioWidget = widget as Audio;

        const onAddAudioAsset = (
          databaseId: string,
          name: string,
          url: string,
          originalUrl: string,
        ) => {
          updateAudioWidget.mutate({
            assetId: databaseId,
            assetName: name,
            detailUrl: url,
            audioUrl: originalUrl,
          });
        };

        return (
          <AudioWidget
            handleAddAudioAsset={onAddAudioAsset}
            assetId={audioWidget.assetId}
            audioUrl={audioWidget.audioUrl}
            assetName={audioWidget.assetName}
            readOnly={readOnly}
          />
        );
      }

      case WIDGET_TYPE.PERSONAL_FILE: {
        const personalFileWidget = widget as PersonalFile;

        return (
          <PersonalFileWidget
            file={personalFileWidget.file}
            columnIndex={columnIndex}
            widgetId={personalFileWidget.id}
            sectionId={sectionId}
          />
        );
      }

      case WIDGET_TYPE.YOUTUBE: {
        const youtubeWidget = widget as TYoutubeWidget;

        return (
          <YoutubeWidget
            sectionId={sectionId}
            columnIndex={columnIndex}
            widget={youtubeWidget}
          />
        );
      }

      case WIDGET_TYPE.FIGMA: {
        const fimgaWidget = widget as Figma;

        return <FigmaWidget widget={fimgaWidget} />;
      }

      default:
        // throw ts error if widget type missing
        assertNever(widget.type);
    }
  };

  const handleWidgetClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    setActiveInspectorSetting(InspectorSetting.WIDGET_VIEW);
    setActiveSectionId(sectionId);
    if (activeWidget?.id !== widget?.id) {
      setWidgetSettings({
        widgetIndex: 0,
        widgetId: widget?.id,
        sectionId,
        columnIndex,
      });
    }
    setIsInspectorOpen(true);
  };

  if (readOnly) {
    return <StyledWidgetV2 $readOnly>{renderWidget()}</StyledWidgetV2>;
  }

  return (
    <Draggable draggableId={widget.id} index={index}>
      {(
        { innerRef, draggableProps, dragHandleProps }: DraggableProvided,
        { isDragging }: DraggableStateSnapshot,
      ) => (
        <StyledWidgetV2
          $readOnly={readOnly}
          $numberOfColumns={numberOfColumns}
          $isDragging={isDragging}
          $isSelectedWidget={widgetSettings?.widgetId === widget?.id}
          onClick={handleWidgetClick}
          $isWidgetInspectorOpen={
            activeInspectorSetting === InspectorSetting.WIDGET_VIEW
          }
          ref={innerRef}
          {...draggableProps}
        >
          <StyledDragAndDropHandle
            $isSelectedWidget={widgetSettings?.widgetId === widget?.id}
            {...dragHandleProps}
          >
            <DesignSystemButton
              isSmall
              aria-label="move widget"
              variant="secondary"
              data-testid="dragWidgetButton"
              icon={<IconDragIndicator />}
            />
          </StyledDragAndDropHandle>
          {renderWidget()}
        </StyledWidgetV2>
      )}
    </Draggable>
  );
};

export default memo(RenderWidgetV2);

const StyledDragAndDropHandle = styled.div<{ $isSelectedWidget: boolean }>`
  visibility: ${props => (props.$isSelectedWidget ? 'visible' : 'hidden')};
  display: flex;
  justify-content: center;
  position: absolute;
  top: -42px;
  z-index: 10;
`;
